import { useRef, useState } from "react";
import { motion, useMotionValue, useSpring, AnimatePresence, useInView } from "framer-motion";
import { MaskText, EASE, Magnetic } from "./ui/motion";
import { Panel, Parallax } from "../journey/Panel";
import BurgerImg from "../image/burgerimg.jpg";
import DoctorImg from "../image/doctorsoffice.jpg";
import LastStandImg from "../image/thelastsantd.jpg";
import OutdoorShootingImg from "../image/outdoorshooting.png";
import DbSchemaImg from "../image/dbschema.png";

const PROJECTS = [
  {
    no: "01",
    title: "Burger Order App",
    year: "Aug 2024",
    desc: "Build your own burger — pick the toppings, sauces and size, watch the price update as you go, then send the order off to the server.",
    url: "https://burger-order-app-phi.vercel.app/",
    tags: ["React", "State"],
    image: BurgerImg,
    accent: "#f59e0b",
  },
  {
    no: "02",
    title: "SymptomLog",
    year: "Sept 2024",
    desc: "A little tool for logging symptoms — their intensity, how often they show up and what kind they are — and seeing it all come together in charts you can actually read.",
    url: "https://symptomlog.vercel.app/",
    tags: ["React", "Charts"],
    image: DoctorImg,
    accent: "#22d3ee",
  },
  {
    no: "03",
    title: "Timeseries Visualization",
    year: "Feb 2025",
    desc: "A Vue dashboard where you edit the data table and the line graph redraws on the spot. Filter by date to zoom in on whatever window you care about.",
    url: "https://timeseries-data-visualization-omega.vercel.app/",
    tags: ["Vue.js", "Dataviz"],
    image: null,
    accent: "#42b883",
  },
  {
    no: "04",
    title: "The Last Stand",
    year: "April 2026",
    desc: "A 3D tower defense game — build and aim cannons to hold off waves of zombies across four difficulties, with bosses ramping up as the swarm grows. WASD to move, Q/E to rotate, click to build.",
    url: "https://thelaststand.vercel.app/",
    tags: ["Three.js", "WebGL"],
    image: LastStandImg,
    accent: "#ef4444",
  },
  {
    no: "05",
    title: "Outdoor Shooting",
    year: "Jun 2026",
    desc: "A basketball arcade game — aim, set the power and let the ball fly to sink shots through the hoop. Line up your angle and rack up baskets.",
    url: "https://outdoorshooting.vercel.app/",
    tags: ["JavaScript", "Game"],
    image: OutdoorShootingImg,
    accent: "#f97316",
  },
  {
    no: "06",
    title: "SchemaFlow",
    year: "Jul 2027",
    desc: "A visual database schema designer — drag tables onto the canvas, wire up relationships by hand and import models straight from C#. Draft your schema on an infinite board with a command palette at your fingertips.",
    url: "https://dbschema-green.vercel.app/",
    tags: ["React", "Tool"],
    image: DbSchemaImg,
    accent: "#6366f1",
  },
];

/* ── Horizontal mode: one cinematic scene per project ─────
   All reveals are driven by ONE observer on the (unclipped)
   scene root — masked children can't observe themselves,
   overflow clipping zeroes their intersection area. */
function ProjectScene({ project }) {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.35 });

  return (
    <Panel horizontal className="group">
      <div ref={ref} className="absolute inset-0">
      {/* backdrop */}
      <motion.div
        className="absolute inset-0"
        animate={{ scale: inView ? 1 : 1.08 }}
        transition={{ duration: 1.4, ease: EASE }}
      >
        {project.image ? (
          <img
            src={project.image}
            alt={project.title}
            className="w-full h-full object-cover opacity-45"
          />
        ) : (
          <div
            className="w-full h-full"
            style={{ background: `linear-gradient(140deg, ${project.accent}2e, #0a0a0b 65%)` }}
          />
        )}
        {/* left-heavy scrim for text */}
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/70 to-ink/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-ink/40" />
      </motion.div>

      {/* giant number watermark */}
      <Parallax horizontal depth={0.12} maxShift={140} className="absolute -top-6 right-8 pointer-events-none select-none">
        <motion.span
          className="display block leading-none text-[24vw] text-paper/[0.045]"
          animate={{ opacity: inView ? 1 : 0 }}
          transition={{ duration: 1.2, delay: 0.3, ease: EASE }}
        >
          {project.no}
        </motion.span>
      </Parallax>

      {/* content */}
      <div className="relative z-10 h-full flex flex-col justify-center pl-14 xl:pl-24 pr-10 max-w-4xl">
        <motion.div
          className="flex items-center gap-4 mb-8"
          animate={{ opacity: inView ? 1 : 0, x: inView ? 0 : -30 }}
          transition={{ duration: 0.7, delay: 0.05, ease: EASE }}
        >
          <span className="font-mono text-xs text-paper-dim">{project.no}</span>
          <span className="h-px w-16" style={{ background: project.accent }} />
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-paper-dim">
            {project.year}
          </span>
        </motion.div>

        <div className="overflow-hidden mb-6">
          <motion.h3
            className="display text-[6.5vw] leading-[0.95] text-paper"
            initial={false}
            animate={{ y: inView ? 0 : "110%" }}
            transition={{ duration: 0.9, delay: 0.12, ease: EASE }}
          >
            {project.title}
          </motion.h3>
        </div>

        <motion.p
          className="text-paper-dim max-w-xl leading-relaxed mb-8 text-base xl:text-lg"
          animate={{ opacity: inView ? 1 : 0, y: inView ? 0 : 24 }}
          transition={{ duration: 0.8, delay: 0.22, ease: EASE }}
        >
          {project.desc}
        </motion.p>

        <motion.div
          className="flex items-center gap-6"
          animate={{ opacity: inView ? 1 : 0, y: inView ? 0 : 16 }}
          transition={{ duration: 0.7, delay: 0.3, ease: EASE }}
        >
          <Magnetic strength={0.4}>
            <a
              href={project.url}
              target="_blank"
              rel="noopener noreferrer"
              data-cursor-label="visit"
              className="inline-flex items-center gap-3 rounded-full border border-paper/25 px-6 py-3 font-mono text-xs uppercase tracking-[0.2em] text-paper hover:text-ink transition-colors duration-300"
              style={{ "--tw-border-opacity": 1 }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = project.accent;
                e.currentTarget.style.borderColor = project.accent;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.borderColor = "";
              }}
            >
              Visit Project
              <span>↗</span>
            </a>
          </Magnetic>
          <div className="flex items-center gap-2">
            {project.tags.map((t) => (
              <span
                key={t}
                className="font-mono text-[10px] uppercase tracking-[0.15em] text-paper-dim border border-paper/15 rounded-full px-3 py-1"
              >
                {t}
              </span>
            ))}
          </div>
        </motion.div>
      </div>

      {/* scene meta — bottom right */}
      <motion.div
        className="absolute bottom-8 right-10 z-10 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.25em] text-paper-dim"
        animate={{ opacity: inView ? 1 : 0 }}
        transition={{ duration: 0.8, delay: 0.4, ease: EASE }}
      >
        <span className="w-8 h-px bg-paper/20" />
        <span>
          Scene {project.no} / {String(PROJECTS.length).padStart(2, "0")}
        </span>
      </motion.div>
      </div>
    </Panel>
  );
}

/* ── Vertical mode pieces (original index rows + preview) ── */
function Preview({ project, mx, my }) {
  return (
    <motion.div
      className="pointer-events-none absolute z-20 hidden lgl:block w-[340px] h-[240px] rounded-lg overflow-hidden shadow-2xl"
      style={{ x: mx, y: my, translateX: "-50%", translateY: "-50%" }}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      transition={{ duration: 0.35, ease: EASE }}
    >
      {project.image ? (
        <img src={project.image} alt={project.title} className="w-full h-full object-cover" />
      ) : (
        <div
          className="w-full h-full grid place-items-center"
          style={{ background: `linear-gradient(140deg, ${project.accent}33, #131315)` }}
        >
          <span className="display italic text-3xl" style={{ color: project.accent }}>
            {project.title}
          </span>
        </div>
      )}
      <span className="absolute inset-0 ring-1 ring-inset ring-acid/40" />
    </motion.div>
  );
}

function Row({ project, onEnter, onLeave }) {
  return (
    <a
      href={project.url}
      target="_blank"
      rel="noopener noreferrer"
      data-cursor-label="visit"
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      className="group block border-t border-paper/10 last:border-b"
    >
      <div className="relative py-8 sm:py-10 px-2 sm:px-4 flex flex-col gap-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-baseline gap-4 sm:gap-8 min-w-0">
            <span className="font-mono text-xs text-paper-dim shrink-0">{project.no}</span>
            <h3 className="display text-[10vw] mdl:text-[5vw] leading-none text-paper group-hover:text-acid transition-colors duration-500 group-hover:translate-x-2 ease-expo truncate">
              {project.title}
            </h3>
          </div>
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-paper-dim shrink-0 hidden sm:block">
            {project.year}
          </span>
        </div>

        <div className="flex flex-col lgl:flex-row lgl:items-end justify-between gap-4 pl-8 sm:pl-16">
          <p className="text-paper-dim max-w-xl leading-relaxed">{project.desc}</p>
          <div className="flex items-center gap-2">
            {project.tags.map((t) => (
              <span
                key={t}
                className="font-mono text-[10px] uppercase tracking-[0.15em] text-paper-dim border border-paper/15 rounded-full px-3 py-1"
              >
                {t}
              </span>
            ))}
          </div>
        </div>

        {/* mobile thumb */}
        <div className="lgl:hidden mt-2 pl-8 sm:pl-16">
          {project.image ? (
            <img
              src={project.image}
              alt={project.title}
              className="w-full max-w-xs rounded-md object-cover aspect-video"
            />
          ) : (
            <div
              className="w-full max-w-xs rounded-md aspect-video grid place-items-center"
              style={{ background: `linear-gradient(140deg, ${project.accent}33, #131315)` }}
            >
              <span className="display italic text-xl" style={{ color: project.accent }}>
                {project.title}
              </span>
            </div>
          )}
        </div>
      </div>
    </a>
  );
}

export default function Projects({ horizontal }) {
  const sectionRef = useRef(null);
  const [active, setActive] = useState(null);

  const mx = useSpring(useMotionValue(0), { stiffness: 200, damping: 22, mass: 0.5 });
  const my = useSpring(useMotionValue(0), { stiffness: 200, damping: 22, mass: 0.5 });

  const handleMove = (e) => {
    const rect = sectionRef.current?.getBoundingClientRect();
    if (!rect) return;
    mx.set(e.clientX - rect.left);
    my.set(e.clientY - rect.top);
  };

  if (horizontal) {
    return (
      <section id="projects" className="flex h-full shrink-0">
        {/* Title card */}
        <Panel horizontal className="flex flex-col justify-center px-6 sm:px-10">
          <div className="flex items-center gap-4 mb-12">
            <span className="eyebrow">(04)</span>
            <span className="h-px w-12 bg-acid" />
            <span className="eyebrow">Selected Work</span>
          </div>
          <Parallax horizontal depth={-0.05}>
            <MaskText
              className="display text-[10vw] leading-[0.95]"
              lineClassName="text-paper"
              lines={[<>Projects</>]}
            />
          </Parallax>
          <p className="mt-8 font-mono text-xs uppercase tracking-[0.2em] text-paper-dim">
            {String(PROJECTS.length).padStart(2, "0")} Shipped · Keep scrolling
          </p>
        </Panel>

        {/* One scene per project */}
        {PROJECTS.map((p, i) => (
          <ProjectScene key={p.no} project={p} index={i} />
        ))}
      </section>
    );
  }

  return (
    <section
      id="projects"
      ref={sectionRef}
      onMouseMove={handleMove}
      className="relative py-24 sm:py-36 px-6 sm:px-10"
    >
      <div className="flex items-center gap-4 mb-12">
        <span className="eyebrow">(04)</span>
        <span className="h-px w-12 bg-acid" />
        <span className="eyebrow">Selected Work</span>
      </div>

      <div className="flex flex-col mdl:flex-row mdl:items-end justify-between gap-6 mb-16">
        <MaskText
          className="display text-[12vw] mdl:text-[7vw] leading-[0.95]"
          lineClassName="text-paper"
          lines={[<>Projects</>]}
        />
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-paper-dim mb-3">
          {String(PROJECTS.length).padStart(2, "0")} Shipped · Hover to preview
        </p>
      </div>

      <div className="relative" onMouseLeave={() => setActive(null)}>
        <AnimatePresence>
          {active !== null && <Preview project={PROJECTS[active]} mx={mx} my={my} />}
        </AnimatePresence>

        {PROJECTS.map((p, i) => (
          <Row
            key={p.no}
            project={p}
            onEnter={() => setActive(i)}
            onLeave={() => {}}
          />
        ))}
      </div>
    </section>
  );
}
